import React, { FC, memo, useCallback, useState } from 'react'
import cn from 'classnames'
import { useStore } from 'store'
import { observer } from 'mobx-react'
import { SectionContainer } from 'containers/SectionContainer'
import { InnerContainer } from 'containers/InnerContainer'
import { HeadsetInfoItem } from 'components/HeadsetInfoItem'
import { TitleSection } from 'components/TitleSection'

import { staticMultimedia } from 'utils'
import { useMediaQuery } from 'utils/hooks'

import { ReactComponent as HeadsetPointer } from 'assets/images/ellipsePointer.svg'

import styles from './styles.module.scss'

interface IProps {
}

enum HeadsetInfo {
  Connectivity,
  USB,
  Mixed_Reality,
  Secure_Anchor
}

const getHeadsetInfo = (type: HeadsetInfo) => {
  switch (type) {
    case HeadsetInfo.Connectivity:
      return {
        key: HeadsetInfo.Connectivity,
        title: 'Connectivity:',
        description: 'Embrace seamless connectivity through our all-in-one USB Type-C cable, a 6-meter (20 ft.) all-in-one hybrid marvel of Display Port, USB 3 Gen 2, and power functionality. Dive into native wired PC Mode, ensuring a lossless experience. And with native Steam VR & OpenXR support, your compatibility knows no bounds.'
      }
    case HeadsetInfo.USB:
      return {
        key: HeadsetInfo.USB,
        title: '3x USB-C 3.2 Gen2 Ports:',
        description: 'Stay connected and expand your capabilities with 3× USB-C (3.2 Gen2) ports, each delivering 10 Gbps for seamless integration of external accessories. Additionally, enjoy audio versatility with a 3.5 mm headphone jack for immersive sound experiences.'
      }
    case HeadsetInfo.Mixed_Reality:
      return {
        key: HeadsetInfo.Mixed_Reality,
        title: 'Mixed Reality:',
        description: 'Step into a mixed reality realm with the power of 2× 12 Mpx  path-through cameras, offering a high-resolution of 4056x3040 pixels. Utilize foveated transport for efficient data transmission and performance. Enjoy a field of view (FOV) spanning 125° horizontally and 100° vertically, providing an uncompromized window to the world around you.'
      }
    case HeadsetInfo.Secure_Anchor:
      return {
        key: HeadsetInfo.Secure_Anchor,
        title: 'Anchor Points:',
        description: 'Elevate your virtual experience with 4 modular and customizable anchor points. These 3D printable anchor points offer personalized attachments for accessories, secured by M2.5 imbus flat head screws for reliable stability.'
      }
  }
}

const ConnectSection: FC<IProps> = props => {
  const width_1024 = useMediaQuery('(max-width: 1024px)')
  const [activeItem, setActiveItem] = useState<number | null>(null)

  const onChangeActiveItem = useCallback((key: number | null) => {
    setActiveItem(key)
  }, [])

  return (
    <SectionContainer withBackgroundImage>
      <InnerContainer>
        <div className={styles.connectContainer}>
          <div className={styles.headsetImagesBlock}>

            <div className={styles.leftBlock}>
              <TitleSection title={'Connect and Modify:'}
                            subTitle={'Unlock the Full Potential of the VR1\'s High-end Specs and Modularity'}/>
              <div className={styles.titleBlock}>
                <div className={styles.title}>

                </div>
                <div className={styles.subTitle}>

                </div>
              </div>

              <div className={styles.headsetFirstBox}>
                <img
                  src={staticMultimedia.connectAndImmerse1}
                  alt="headset"/>
                <HeadsetPointer className={cn({
                  [styles.connectivityPointerOne]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Connectivity
                })}/>
                <HeadsetPointer className={cn({
                  [styles.USBPointerOne]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.USB
                })}/>
                <HeadsetPointer className={cn({
                  [styles.USBPointerTwo]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.USB
                })}/>
                <HeadsetPointer className={cn({
                  [styles.mixedRealityPointerOne]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Mixed_Reality
                })}/>
                <HeadsetPointer className={cn({
                  [styles.mixedRealityPointerTwo]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Mixed_Reality
                })}/>
                <HeadsetPointer className={cn({
                  [styles.secureAnchorPointerOne]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Secure_Anchor
                })}/>
                <HeadsetPointer className={cn({
                  [styles.secureAnchorPointerTwo]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Secure_Anchor
                })}/>
              </div>
              {width_1024
                ? <div className={styles.headsetInfoBlock}>
                  <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.Connectivity)}
                                   changeActiveItem={onChangeActiveItem}/>
                  <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.USB)} changeActiveItem={onChangeActiveItem}/>
                </div>
                : null}
            </div>

            <div className={styles.rightBlock}>
              <div className={styles.headsetSecondBox}>
                <img
                  src={staticMultimedia.connectAndImmerse2}
                  alt="headset"/>
                <HeadsetPointer className={cn({
                  [styles.connectivityPointerTwo]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Connectivity
                })}/>
                <HeadsetPointer className={cn({
                  [styles.USBPointerThree]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.USB
                })}/>
                <HeadsetPointer className={cn({
                  [styles.secureAnchorPointerThree]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Secure_Anchor
                })}/>
                <HeadsetPointer className={cn({
                  [styles.secureAnchorPointerFour]: true,
                  [styles.headsetPointerActive]: activeItem === HeadsetInfo.Secure_Anchor
                })}/>
              </div>
              {width_1024
                ? <div className={styles.headsetInfoBlock}>
                  <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.Mixed_Reality)}
                                   changeActiveItem={onChangeActiveItem}/>
                  <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.Secure_Anchor)}
                                   changeActiveItem={onChangeActiveItem}/>
                </div>
                : null}
            </div>
          </div>

          {!width_1024
            ? <div className={styles.headsetInfoBlock}>
              <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.Connectivity)} changeActiveItem={onChangeActiveItem}/>
              <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.USB)} changeActiveItem={onChangeActiveItem}/>
              <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.Mixed_Reality)} changeActiveItem={onChangeActiveItem}/>
              <HeadsetInfoItem info={getHeadsetInfo(HeadsetInfo.Secure_Anchor)} changeActiveItem={onChangeActiveItem}/>
            </div>
            : null}
        </div>
      </InnerContainer>
    </SectionContainer>
  )
}

export default memo(ConnectSection)
